import { Box, Divider, FormControl, MenuItem, Select, Tooltip } from '@mui/material';
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from 'material-react-table';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useTranslation } from 'react-i18next';
import HistoryIcon from '@mui/icons-material/History';
import RuleIcon from '@mui/icons-material/Rule';
import { useSelector } from 'react-redux';
import { selectGetTestRunsStatus, selectTestRuns } from '@pages/testruns/store/selectors';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useNavigate } from 'react-router-dom';
import {
    UI_FUNCTIONAL_TESTING_PATH,
    UI_HISTORY_PATH,
    UI_TEST_CASE_PATH,
    UI_TEST_PLAN_PATH,
    UI_TEST_RUN_PATH,
} from '@shared/constants/pathsConstants';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import { useMemo, useState } from 'react';
import RefreshButton from './RefreshButton';
import { TestrunResultVerifyDialog } from './TestrunResultVerifyDialog';
import UtmsButton from '@shared/components/ui/UtmsButton';
import ToggleMoreFiltersButton from '@shared/components/others/ToggleMoreFiltersButton/ToggleMoreFiltersButton';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeployRunModalForTests from '@pages/stands/components/DeployModal/DeployRunModalForTests';
import { BASE_URL } from '@shared/constants/urlsConstants';

const TestrunsTableToolbar = ({ table, selectedTestRunId, createdAtLast, setCreatedAtLast, fetchData }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const testRunFullObject = useSelector(selectTestRuns).find((item) => item.pk === Number(selectedTestRunId));
    const getTestRunsStatus = useSelector(selectGetTestRunsStatus);
    const testplan = testRunFullObject?.testplan;
    const testcase = testRunFullObject?.testcase;
    const results = testRunFullObject?.results;
    const firstResult = results?.[0];
    const isHandtest = testcase?.is_handtest;
    const isFailedNA = firstResult?.result === 'failed_na';
    const failedNACount = testRunFullObject?.failed_na_cnt;
    const isTestRunTestPlan = useMemo(
        () => selectedTestRunId && testRunFullObject?.testplan,
        [selectedTestRunId, testRunFullObject?.testplan],
    );
    const [openTestcaseRunModal, setOpenTestcaseRunModal] = useState(false);
    const disabled =
        !testRunFullObject ||
        (testcase && (isHandtest || !firstResult || !isFailedNA)) ||
        (testplan && failedNACount === 0);
    const disableReason =
        testcase && (isHandtest || !isFailedNA)
            ? t('selected_run_must_be_auto_test_with_result_failed_na')
            : testplan && failedNACount === 0
              ? t('selected_plan_must_have_auto_tests_with_result_failed_na')
              : '';
    const allureUrl =
        testRunFullObject?.allure_url && `${BASE_URL}${UI_TEST_RUN_PATH}/${testRunFullObject?.pk}/allure/`;

    const handleRefreshTestruns = () => {
        fetchData('refresh button');
    };

    const handleOpenDeployRunModal = () => {
        setOpenTestcaseRunModal(true);
    };

    return (
        <>
            <DeployRunModalForTests
                open={openTestcaseRunModal}
                setOpen={setOpenTestcaseRunModal}
                entityType="testcase"
                entity={testcase}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <GlobalFilterTextField table={table} placeholder={t('test_run') + '...'} />
                        <FormControl size="small" sx={{ width: 224 }}>
                            <Select
                                value={createdAtLast}
                                size="small"
                                sx={{ width: '100%' }}
                                IconComponent={() => <DateRangeIcon sx={{ color: 'var(--icon-secondary)', mr: 1.5 }} />}
                                onChange={(e) => setCreatedAtLast(e.target.value)}
                            >
                                <MenuItem value={'day'}>{t('last_24_hours')}</MenuItem>
                                <MenuItem value={'week'}>{t('last_week')}</MenuItem>
                                <MenuItem value={'month'}>{t('last_month')}</MenuItem>
                                <MenuItem value={'all'}>{t('all')}</MenuItem>
                            </Select>
                        </FormControl>
                        <ToggleMoreFiltersButton table={table} />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip
                            title={
                                (testplan || !testRunFullObject) && selectedTestRunId
                                    ? t('restart_available_only_for_testcase_runs')
                                    : ''
                            }
                        >
                            <span>
                                <UtmsButton
                                    size="small"
                                    disabled={testplan || !testRunFullObject}
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleOpenDeployRunModal}
                                >
                                    {t('rerun')}
                                </UtmsButton>
                            </span>
                        </Tooltip>
                        <UtmsButton
                            startIcon={<HistoryIcon />}
                            disabled={!selectedTestRunId}
                            onClick={() => {
                                navigate(
                                    isTestRunTestPlan
                                        ? `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${testRunFullObject.testplan.pk}/${UI_HISTORY_PATH}`
                                        : `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testRunFullObject.testcase.pk}/${UI_HISTORY_PATH}`,
                                );
                            }}
                        >
                            {t('history')}
                        </UtmsButton>
                        <Divider orientation="vertical" flexItem />
                        <UtmsButton
                            startIcon={isTestRunTestPlan ? <RuleIcon /> : <ScienceOutlinedIcon />}
                            disabled={!selectedTestRunId}
                            component="a"
                            href={
                                isTestRunTestPlan
                                    ? `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${testRunFullObject?.testplan.pk}`
                                    : `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testRunFullObject?.testcase.pk}`
                            }
                            onClick={() => {
                                navigate(
                                    isTestRunTestPlan
                                        ? `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${testRunFullObject.testplan.pk}`
                                        : `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testRunFullObject.testcase.pk}`,
                                );
                            }}
                        >
                            {isTestRunTestPlan ? t('go_to_testplan_definition') : t('go_to_testcase_definition')}
                        </UtmsButton>
                        <Divider orientation="vertical" flexItem />
                        <TestrunResultVerifyDialog
                            testrun={testRunFullObject}
                            testrunId={testRunFullObject?.pk}
                            verifyTestPlan
                            createdAtLast={createdAtLast}
                            forceInitialResult="failed_na"
                            disabled={disabled}
                            disableReason={disableReason}
                        />
                        <Divider orientation="vertical" flexItem />
                        <UtmsButton
                            component={'a'}
                            href={testRunFullObject?.allure_url ? allureUrl : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={
                                !selectedTestRunId ||
                                testRunFullObject?.testcase?.is_handtest ||
                                !testRunFullObject?.allure_url
                            }
                            size="small"
                            startIcon={<HistoryOutlinedIcon />}
                        >
                            {t('autotest_details')}
                        </UtmsButton>
                        <Divider orientation="vertical" flexItem />
                        <RefreshButton
                            getStatus={getTestRunsStatus}
                            disabled={getTestRunsStatus !== REQUEST_STATUS.SUCCESS}
                            onClick={handleRefreshTestruns}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default TestrunsTableToolbar;
